import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SellOutlined } from '@mui/icons-material';
import { PriceSuggestionType } from '@retail/products/types';
import { PrioritizedSalesPriceCondition } from './PrioritizedSalesPriceCondition';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { getPriceSuggestionType } from '@retail/products/utils';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { MarkupPriority } from './MarkupPriotity';
import { ActiveMarkupTooltip } from '../ActiveMarkupTooltip';
import { MarkupBinding } from '@retail/calculus/types';

interface Props {
  selectedPriceSuggestionTypes: PriceSuggestionType[];
  currentMarkup: number | null;
  markupCondition?: MarkupBinding;
  getPriceTypeValue: (priceType: PriceSuggestionType) => string;
  handleReprioritizedPriceSuggestions: (priceTypeIndex: number, prioritize: 'UP' | 'DOWN') => void;
  checkPriceType: (priceType: PriceSuggestionType) => void;
  setMarkupSalesCoverageDegree: (value: string | null) => void;
}

export function SalesPriceConditionPriority({
  selectedPriceSuggestionTypes,
  currentMarkup,
  markupCondition,
  getPriceTypeValue,
  handleReprioritizedPriceSuggestions,
  checkPriceType,
  setMarkupSalesCoverageDegree,
}: Props) {
  const t = useCalculusTFunction();
  const priceSuggestionT = usePriceSuggestionTFunction();

  return (
    <Stack flexGrow={1}>
      <Stack
        height={50}
        width="100%"
        direction="row"
        alignItems="center"
        gap={1.5}
        px={3}
        sx={(theme) => ({
          bgcolor: theme.vars
            ? `rgba(${theme.vars.palette.success.mainChannel} / 0.08)`
            : alpha(theme.palette.success.main, 0.08),
        })}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SellOutlined fontSize="small" />
        </Box>
        <Typography variant="subtitle2">
          {t('calculus.salesPriceCondition.prioritizedSalesPriceConditions')}
        </Typography>
      </Stack>
      <Stack px={3} py={2} gap={1}>
        {selectedPriceSuggestionTypes.map((priceType, index) => (
          <Stack key={priceType}>
            {priceType === 'MARKUP' ? (
              <PrioritizedSalesPriceCondition
                conditionType={t('calculus.markup')}
                conditionValue={
                  <MarkupPriority
                    coverageDegree={currentMarkup}
                    setCoverageDegree={setMarkupSalesCoverageDegree}
                  />
                }
                priority={index + 1}
                removeCondition={() => {
                  setMarkupSalesCoverageDegree(null);
                  checkPriceType('MARKUP');
                }}
              />
            ) : (
              <PrioritizedSalesPriceCondition
                conditionType={getPriceSuggestionType(priceSuggestionT, priceType)}
                conditionValue={
                  <Typography variant="body2">{getPriceTypeValue(priceType)}</Typography>
                }
                priority={index + 1}
                totalPrioritySuggestions={
                  selectedPriceSuggestionTypes.includes('MARKUP')
                    ? selectedPriceSuggestionTypes.length - 1
                    : selectedPriceSuggestionTypes.length
                }
                removeCondition={() => checkPriceType(priceType)}
                handleReprioritizedPriceSuggestions={
                  selectedPriceSuggestionTypes.filter((t) => t !== 'MARKUP').length > 1
                    ? handleReprioritizedPriceSuggestions
                    : undefined
                }
              />
            )}
          </Stack>
        ))}
        {markupCondition?.isInherited && !selectedPriceSuggestionTypes.includes('MARKUP') && (
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={(theme) => ({
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: theme.vars
                ? `rgba(${theme.vars.palette.success.mainChannel} / 0.25)`
                : alpha(theme.palette.success.main, 0.25),
              bgcolor: theme.vars
                ? `rgba(${theme.vars.palette.success.mainChannel} / 0.06)`
                : alpha(theme.palette.success.main, 0.06),
            })}
          >
            <Box
              sx={(theme) => ({
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.vars
                  ? `rgba(${theme.vars.palette.success.mainChannel} / 0.15)`
                  : alpha(theme.palette.success.main, 0.15),
                color: 'success.main',
                fontWeight: 'bold',
                fontSize: 13,
              })}
            >
              {selectedPriceSuggestionTypes.length + 1}
            </Box>

            <Stack direction="row" alignItems="center" gap={4} py={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body1">{t('calculus.markup')}</Typography>
                <ActiveMarkupTooltip bindingInfo={markupCondition.bindingInfo} />
              </Stack>
              <Typography variant="body2">{`${t('calculus.coverageDegree')}: ${
                markupCondition.coverageDegree
              } %`}</Typography>
              <Typography variant="body2">{`${t('calculus.customerMarkup.factor')}: ${
                markupCondition.salesFactor ? markupCondition.salesFactor.toFixed(3) : '-'
              }`}</Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
